import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { LiveEvent } from '@/pages/LiveEvent/store/liveEvent/types';
import { useChangeEnrollmentLocationMutationRemote } from '@/store/v2';
import { SharedSnackbarContext } from '@/component/SharedSnackbar/SharedSnackbar';

type LiveEventLocationsContextValue = {
  locations: LiveEvent['locations'];
  activeLocation?: LiveEvent['locations'][number];
  setActiveLocation: (args: { id: number }) => void;
  getLocationNameById: (id: number | undefined | null) => string;
  changeEnrollmentToLocation: (args: { newLocationId: number }) => Promise<{
    enrollmentId?: number;
    locationId?: number;
  }>;
  isChangingEnrollment: boolean;
};

const LiveEventLocationsContext = createContext<LiveEventLocationsContextValue | undefined>(undefined);

type LiveEventLocationsProviderProps = {
  children: React.ReactNode;
  locations: LiveEvent['locations'];
};

export const LiveEventLocationsProvider = ({ children, locations }: LiveEventLocationsProviderProps): JSX.Element => {
  const { openSnackbar } = useContext(SharedSnackbarContext);

  const initialActiveFromEnrollment = useMemo(() => {
    const enrolled = locations.find((l) => l.myEnrollment);
    return enrolled ? enrolled.id : (locations?.[0]?.id ?? null);
  }, [locations]);

  const [activeLocationId, setActiveLocationId] = useState<number | null>(initialActiveFromEnrollment);

  const activeLocation = useMemo(() => locations.find((l) => l.id === activeLocationId), [locations, activeLocationId]);

  const [changeEnrollmentLocation, { loading: isChangingEnrollment }] = useChangeEnrollmentLocationMutationRemote();

  useEffect(() => {
    if (!activeLocationId || !locations.some((l) => l.id === activeLocationId)) {
      setActiveLocationId(initialActiveFromEnrollment);
    }
  }, [locations, activeLocationId, initialActiveFromEnrollment]);

  const changeEnrollmentToLocation = useCallback(
    async ({ newLocationId }: { newLocationId: number }) => {
      const currentEnrollmentId = locations.find((l) => l.myEnrollment)?.myEnrollment?.id;

      if (!currentEnrollmentId) {
        return {};
      }

      try {
        const { data } = await changeEnrollmentLocation({
          variables: {
            input: {
              enrollmentId: currentEnrollmentId,
              newLocationId,
            },
          },
          update(cache, { data }) {
            if (data?.changeEnrollmentLocation?.id) {
              cache.evict({ fieldName: 'myEnrollments' });
            }
          },
        });

        openSnackbar({ message: 'Your seat has been updated', isDismissive: true });

        setActiveLocationId(newLocationId);

        return {
          enrollmentId: data?.changeEnrollmentLocation?.id,
          locationId: data?.changeEnrollmentLocation?.locationId,
        };
      } catch {
        return {};
      }
    },
    [locations, changeEnrollmentLocation],
  );

  const getLocationNameById = useCallback(
    (id: number | undefined | null) => {
      if (!id) return 'Session';
      const locationIndex = locations.findIndex((l) => l.id === id);
      return `Session ${locationIndex + 1}`;
    },
    [locations],
  );

  const value = useMemo<LiveEventLocationsContextValue>(
    () => ({
      locations,
      activeLocation,
      setActiveLocation: ({ id }) => setActiveLocationId(id),
      getLocationNameById,
      changeEnrollmentToLocation,
      isChangingEnrollment,
    }),
    [locations, activeLocationId, activeLocation, changeEnrollmentToLocation, isChangingEnrollment],
  );

  return <LiveEventLocationsContext.Provider value={value}>{children}</LiveEventLocationsContext.Provider>;
};

export const useLiveEventLocations = (): LiveEventLocationsContextValue => {
  const ctx = useContext(LiveEventLocationsContext);
  if (!ctx) {
    throw new Error('useLiveEventLocations must be used within LiveEventLocationsProvider');
  }
  return ctx;
};
